import type { ReactNode } from 'react';
import type { MessageGeneration } from '../types/chat';
import { hasInlineStreamSteps } from '../utils/stream-status-entries';
import { SeliseAssistantLogo } from './MessageLoading';
import { AssistantWorkingStatus } from './AssistantWorkingStatus';

interface AssistantStreamingRowProps {
  readonly generation?: MessageGeneration;
  readonly children?: ReactNode;
}

/** Logo + "Working" until assistant text or inline Steps exist. */
export function shouldRenderAssistantStreamingRow(
  message: { role: string },
  generation: MessageGeneration | undefined,
  isStillGenerating: boolean
): boolean {
  if (message.role !== 'assistant' || !isStillGenerating || !generation || generation.isCancelled) {
    return false;
  }
  // Compaction in progress replaces the live bubble so the timeline can hide.
  if (generation.compactionStreamStatus === 'started') {
    return true;
  }
  if (typeof generation?.content === 'string' && generation.content.trim()) {
    return false;
  }
  // First tool/skill status uses inline Steps immediately — not the Working row.
  if (hasInlineStreamSteps(generation.streamStatusEntries)) {
    return false;
  }
  if (
    generation.compactionStreamStatus === 'completed' ||
    generation.compactionStreamStatus === 'failed'
  ) {
    return true;
  }
  return (
    generation.isThisGenerationOnGoing === true ||
    generation.skillStreamStatus != null ||
    generation.toolCallStreamStatus === 'started' ||
    generation.toolCallStreamStatus === 'completed' ||
    generation.subagentStreamStatus === 'started' ||
    generation.subagentStreamStatus === 'completed' ||
    (generation.streamStatusEntries?.length ?? 0) > 0 ||
    !generation.toolCallStreamStatus
  );
}

export function AssistantStreamingRow({ generation, children }: AssistantStreamingRowProps) {
  return (
    <div className="flex items-start gap-4" role="status" aria-live="polite">
      <SeliseAssistantLogo />
      {children ?? (
        <AssistantWorkingStatus
          entries={generation?.streamStatusEntries}
          compactionStreamStatus={generation?.compactionStreamStatus}
        />
      )}
    </div>
  );
}
